import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/Button';
import { Card, CardSub, CardTitle } from '@/components/Card';
import { Input, Label } from '@/components/Input';
import { useAuth } from '@/lib/auth';
import { callCompleteOnboarding } from '@/lib/api';

// Onboarding — REQUIRED profile completion gate.
//
// New users must finish this before they can reach /dashboard. The route gate
// in router.tsx (OnboardingGate / ProtectedGate) enforces it: any signed-in
// user without `onboardingComplete: true` is bounced here. There is NO skip
// path — the Skip button was removed because the team needs full contact
// information (phone) for accountability and support outreach.
//
// Required fields: name, phone (US format), state. Target exam date stays
// optional because not every learner has one scheduled yet.

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID',
  'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS',
  'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK',
  'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV',
  'WI', 'WY', 'DC',
];

// Format raw digits as (xxx) xxx-xxxx as the user types. Strips a leading
// "+1" / "1" country code so a paste of "+1 555 123 4567" or "15551234567"
// formats as "(555) 123-4567" — the platform is US-only and the country
// code adds no information here.
function formatPhone(raw: string): string {
  let digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  digits = digits.slice(0, 10);
  const a = digits.slice(0, 3);
  const b = digits.slice(3, 6);
  const c = digits.slice(6, 10);
  if (digits.length === 0) return '';
  if (digits.length < 4) return `(${a}`;
  if (digits.length < 7) return `(${a}) ${b}`;
  return `(${a}) ${b}-${c}`;
}

function phoneDigits(formatted: string): string {
  return formatted.replace(/\D/g, '');
}

export function Onboarding() {
  const { user, profile } = useAuth();
  const nav = useNavigate();
  const [displayName, setDisplayName] = useState(profile?.displayName ?? user?.displayName ?? '');
  const [phone, setPhone] = useState<string>(formatPhone(profile?.phoneNumber ?? ''));
  const [state, setState] = useState(profile?.state ?? '');
  const [targetExamDate, setTargetExamDate] = useState<string>(
    profile?.targetExamDate
      ? new Date(profile.targetExamDate).toISOString().slice(0, 10)
      : '',
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const phoneOk = phoneDigits(phone).length === 10;
  const nameOk = displayName.trim().length >= 2;
  const stateOk = state.length > 0;
  const canSubmit = useMemo(() => phoneOk && nameOk && stateOk && !busy, [phoneOk, nameOk, stateOk, busy]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!user) return;
    if (!nameOk) {
      setErr('Please enter your name.');
      return;
    }
    if (!phoneOk) {
      setErr('Please enter a valid 10-digit US phone number.');
      return;
    }
    if (!stateOk) {
      setErr('Please pick the state where you are testing.');
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      await callCompleteOnboarding({
        displayName: displayName.trim(),
        phoneNumber: phoneDigits(phone),
        state,
        targetExamDate,
        termsAccepted: true,
        privacyAcknowledged: true,
        professionalDisclaimerAccepted: true,
        appVersion: 'web',
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      nav('/starting-check', { replace: true });
    } catch (e) {
      setErr((e as Error).message ?? 'Could not save your profile.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-start sm:items-center justify-center px-4 py-8 bg-stone-50 dark:bg-stone-950">
      <div className="max-w-md w-full">
        <header className="text-center mb-5">
          <h1 className="text-2xl font-bold mb-1">Complete your profile</h1>
          <p className="text-sm text-stone-600 dark:text-stone-400">
            All fields below are required so we can personalize your study plan and reach you with exam-day reminders.
          </p>
        </header>

        <Card>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label htmlFor="displayName">
                Full name <span className="text-rose-600">*</span>
              </Label>
              <Input
                id="displayName"
                type="text"
                autoComplete="name"
                required
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g. Maria Hernandez"
              />
            </div>

            <div>
              <Label htmlFor="phone">
                Mobile phone <span className="text-rose-600">*</span>
              </Label>
              <Input
                id="phone"
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                required
                value={phone}
                onChange={(e) => setPhone(formatPhone(e.target.value))}
                placeholder="(555) 123-4567"
              />
              <CardSub className="mt-1.5 text-xs">
                Used only for account security and exam-day reminders. We never sell your number.
              </CardSub>
            </div>

            <div>
              <Label htmlFor="state">
                Testing state <span className="text-rose-600">*</span>
              </Label>
              <select
                id="state"
                value={state}
                onChange={(e) => setState(e.target.value)}
                required
                className="block w-full min-h-tap rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-base shadow-sm dark:bg-stone-900 dark:border-stone-700 dark:text-stone-100"
              >
                <option value="">Select your state…</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <CardSub className="mt-1.5 text-xs">
                We prioritize content relevant to your state's exam.
              </CardSub>
            </div>

            <div>
              <Label htmlFor="targetExamDate">When's your exam? <span className="text-stone-400 font-normal">(optional)</span></Label>
              <Input
                id="targetExamDate"
                type="date"
                value={targetExamDate}
                onChange={(e) => setTargetExamDate(e.target.value)}
              />
              <CardSub className="mt-1.5 text-xs">
                We'll use this to pace your practice. You can change it later.
              </CardSub>
            </div>

            {err && <p className="text-sm text-rose-600 dark:text-rose-400">{err}</p>}

            <div className="pt-2">
              <Button type="submit" size="lg" loading={busy} disabled={!canSubmit} className="w-full">
                {canSubmit || busy ? 'Save and continue' : 'Fill in all required fields'}
              </Button>
            </div>
          </form>
        </Card>

        <Card className="mt-4">
          <CardTitle>What's next</CardTitle>
          <CardSub>
            Once your profile is saved, your 36-question Starting Check will build your first personalized study profile.
          </CardSub>
        </Card>
      </div>
    </div>
  );
}
